import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: string;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-primary-500 text-background-50 hover:bg-primary-600 focus-visible:ring-primary-400 dark:text-foreground-950',
  accent:
    'bg-accent-500 text-background-50 hover:bg-accent-600 focus-visible:ring-accent-400 dark:text-foreground-950',
  secondary:
    'bg-secondary-100 text-secondary-900 hover:bg-secondary-200 focus-visible:ring-secondary-300',
  outline:
    'bg-background-50 text-foreground-800 border border-background-300 hover:bg-background-100 hover:border-background-400 focus-visible:ring-secondary-300',
  ghost:
    'bg-transparent text-foreground-700 hover:bg-background-100 focus-visible:ring-secondary-300',
  danger:
    'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-400',
};

const SIZES: Record<Size, string> = {
  sm: 'h-9 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-sm gap-2',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  loading = false,
  fullWidth = false,
  disabled,
  className = '',
  children,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      type="button"
      disabled={isDisabled}
      className={[
        'inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background-50',
        'cursor-pointer disabled:cursor-not-allowed disabled:opacity-55',
        VARIANTS[variant],
        SIZES[size],
        fullWidth ? 'w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {loading ? (
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-base"></i>
        </span>
      ) : (
        icon &&
        iconPosition === 'left' && (
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={`${icon} text-base`}></i>
          </span>
        )
      )}
      {children}
      {icon && iconPosition === 'right' && !loading && (
        <span className="w-4 h-4 flex items-center justify-center">
          <i className={`${icon} text-base`}></i>
        </span>
      )}
    </button>
  );
}